export default {
    name: 'SignatureField',
    props: {
        fieldKey: {
            type: String,
            required: true
        },
        consentText: {
            type: String,
            default: ''
        },
        signatureModes: {
            type: Array,
            default: () => ['draw']
        },
        isRequired: {
            type: Boolean,
            default: false
        },
        label: {
            type: String,
            default: ''
        },
        value: {
            type: String | Object,
            default: ''
        }
    },
    data() {
        return {
            signatureValue: this.value || '',
            timestamp: null,
            signatureEl: null
        }
    },
    computed: {
        uploadAriaLabel() {
            const labelPrefix = this.label ? this.label + ' — ' : ''
            return labelPrefix + this.$t('form_builder.a11y.signature_upload')
        },
        availableModes() {
            let modes = this.signatureModes && this.signatureModes.length > 0 ? this.signatureModes : ['draw']
            modes = modes.map(mode => (mode === 'typing' ? 'draw' : mode))
            return modes.filter(mode => mode !== 'image')
        },
        vueformSchema() {
            return {
                signature: {
                    type: 'signature',
                    name: 'signature',
                    modes: this.availableModes,
                    colors: ['#000000', '#2558b2', '#f22f30'],
                    fonts: ['Caveat@400', 'Sacramento@400', 'Dancing Script@400'],
                    maxWidth: '100%',
                    height: 160,
                    line: true,
                    canClear: true,
                    canUndo: true,
                    canDrop: true,
                    submit: false,
                    maxSize: 2048, // Max file size in KB
                    default: this.value || null,
                    onChange: (newValue, oldValue, el$) => {
                        this.handleChange(newValue)
                    },
                    onClear: (el$) => {
                        this.handleChange(null)
                        this.clearError()
                    }
                }
            }
        }
    },
    mounted() {
        // Intercept file input before Vueform processes it
        this.$nextTick(() => {
            this.setupFileInterception()
        })

        this.$nextTick(() => {
            const vueform = this.$refs.vueform
            if (!vueform) return

            const signatureEl = vueform.el$('signature')
            if (!signatureEl) return

            this.signatureEl = signatureEl

            signatureEl.on('change', (newValue, oldValue, el$) => {
                this.handleChange(newValue)
            })

            signatureEl.on('clear', (el$) => {
                this.handleChange(null)
            })

            signatureEl.on('updated', (el$) => {
                const currentValue = signatureEl.value
                if (currentValue && currentValue !== this.signatureValue) {
                    this.handleChange(currentValue)
                }
            })
        })
    },
    watch: {
        value(newVal) {
            // Update internal value when prop changes
            if (newVal !== this.signatureValue) {
                this.$set(this, 'signatureValue', newVal || '')
            }
        }
    },
    methods: {
        handleChange(newValue) {
            // Clear error when value changes
            this.clearError()

            if (newValue instanceof Blob) {
                // Check file size before processing
                const maxSizeKB = 2048
                const fileSizeKB = newValue.size / 1024

                if (fileSizeKB > maxSizeKB) {
                    this.setError(`Max file size is ${maxSizeKB} KB`)
                    return
                }

                const reader = new FileReader()
                reader.onload = (e) => {
                    const base64Value = e.target.result
                    // Send timestamp as number (milliseconds since epoch)
                    this.timestamp = Date.now()
                    this.$set(this, 'signatureValue', base64Value)
                    this.$emit('input', {
                        value: base64Value,
                        consent: this.consentText,
                        timestamp: this.timestamp
                    })
                }
                reader.onerror = () => {
                    this.$set(this, 'signatureValue', '')
                    this.timestamp = null
                    this.$emit('input', {
                        value: '',
                        consent: this.consentText,
                        timestamp: null
                    })
                }
                reader.readAsDataURL(newValue)
            } else if (!newValue) {
                this.$set(this, 'signatureValue', '')
                this.timestamp = null
                this.$emit('input', {
                    value: '',
                    consent: this.consentText,
                    timestamp: null
                })
            } else if (typeof newValue === 'string') {
                if (newValue && newValue !== this.signatureValue) {
                    // Send timestamp as number (milliseconds since epoch)
                    this.timestamp = Date.now()
                }
                this.$set(this, 'signatureValue', newValue)
                this.$emit('input', {
                    value: newValue,
                    consent: this.consentText,
                    timestamp: this.timestamp
                })
            }
        },
        setupFileInterception() {
            const wrapper = this.$el
            if (!wrapper) return

            const checkAndSetup = () => {
                const fileInput = wrapper.querySelector('input[type="file"]')
                if (fileInput && !fileInput.dataset.intercepted) {
                    fileInput.dataset.intercepted = 'true'
                    fileInput.addEventListener('change', (e) => {
                        const file = e.target.files?.[0]
                        if (file && file.size / 1024 > 2048) {
                            e.stopImmediatePropagation()
                            e.preventDefault()
                            e.target.value = ''
                            this.setError('Max file size is 2048 KB')
                            return false
                        }
                    }, true) // Capture phase - before Vueform
                }

                // Vueform leaves the hidden upload <input type="file"> without an
                // accessible name, so give it one for screen readers.
                if (fileInput && !fileInput.getAttribute('aria-label')) {
                    fileInput.setAttribute('aria-label', this.uploadAriaLabel)
                }

                // Name the signature widget after the field's own label when present,
                // overriding Vueform's generic default.
                const signatureWrapper = wrapper.querySelector('.vf-signature')
                if (signatureWrapper && this.label && !signatureWrapper.dataset.labelled) {
                    signatureWrapper.dataset.labelled = 'true'
                    signatureWrapper.setAttribute('aria-label', this.label)
                }

                // Vueform's color palette listbox has no accessible name of its own
                // (only the individual color options do), so name the group too.
                const colors = wrapper.querySelector('.vf-signature-colors')
                if (colors && !colors.getAttribute('aria-label')) {
                    colors.setAttribute('aria-label', this.$t('form_builder.a11y.signature_color'))
                }

                this.syncTabbability(wrapper)
                this.enableKeyboardActivation(wrapper)

                const block = wrapper.querySelector('.vf-signature')
                if (block) block.setAttribute('tabindex', '-1')
                wrapper.querySelectorAll('.vf-addon-options-wrapper').forEach((trigger) => {
                    if (trigger.getBoundingClientRect().height > 0) {
                        trigger.setAttribute('tabindex', '0')
                    }
                })
            }

            checkAndSetup()
            // Check again after Vueform renders
            setTimeout(checkAndSetup, 100)
            setTimeout(checkAndSetup, 500)

            if (!this._typefaceSyncBound) {
                this._typefaceSyncBound = true
                document.addEventListener('click', (event) => {
                    if (!event.target.closest || !event.target.closest('.vf-addon-options-option')) return
                    const sync = () => wrapper.querySelectorAll('.vf-addon-options-wrapper')
                        .forEach((trigger) => this.syncTypefaceLabel(trigger))
                    sync()
                    requestAnimationFrame(sync)
                    ;[30, 100].forEach((delay) => setTimeout(sync, delay))
                })
            }

            wrapper.addEventListener('click', () => {
                setTimeout(() => {
                    this.syncTabbability(wrapper)
                    this.enableKeyboardActivation(wrapper)

                    const block = wrapper.querySelector('.vf-signature')
                    if (block) block.setAttribute('tabindex', '-1')
                    wrapper.querySelectorAll('.vf-addon-options-wrapper').forEach((trigger) => {
                        if (trigger.getBoundingClientRect().height > 0) {
                            trigger.setAttribute('tabindex', '0')
                        }
                    })
                }, 50)
            })
        },
        syncTabbability(wrapper) {
            if (!wrapper) return

            const focusable = wrapper.querySelectorAll('a[href],button,input,select,textarea,[tabindex]')
            focusable.forEach((el) => {
                const rect = el.getBoundingClientRect()
                const style = window.getComputedStyle(el)
                const visible = rect.width > 0 && rect.height > 0
                    && style.display !== 'none'
                    && style.visibility !== 'hidden'

                if (!visible) {
                    if (el.dataset.origTabindex === undefined) {
                        el.dataset.origTabindex = el.hasAttribute('tabindex')
                            ? el.getAttribute('tabindex')
                            : ''
                    }
                    el.setAttribute('tabindex', '-1')
                } else if (el.dataset.origTabindex !== undefined) {
                    if (el.dataset.origTabindex === '') {
                        el.removeAttribute('tabindex')
                    } else {
                        el.setAttribute('tabindex', el.dataset.origTabindex)
                    }
                    delete el.dataset.origTabindex
                }
            })
        },
        enableKeyboardActivation(wrapper) {
            if (!wrapper) {
                return
            }

            wrapper.querySelectorAll('.vf-signature-colors [role="option"], .vf-signature-clear').forEach((el) => {
                if (el.dataset.keyboardActivation === 'true') return
                el.dataset.keyboardActivation = 'true'
                el.addEventListener('keydown', (event) => {
                    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
                        event.preventDefault()
                        el.click()
                    }
                })
            })

            wrapper.querySelectorAll('.vf-addon-options-wrapper').forEach((trigger) => {
                if (trigger.dataset.keyboardActivation === 'true') return
                trigger.dataset.keyboardActivation = 'true'
                trigger.addEventListener('keydown', (event) => {
                    const key = event.key
                    if (key === 'ArrowUp') {
                        event.preventDefault()
                        event.stopImmediatePropagation()
                        this.stepTypeface(trigger, -1)
                    } else if (key === 'ArrowDown' || key === 'Enter' || key === ' ' || key === 'Spacebar') {
                        event.preventDefault()
                        event.stopImmediatePropagation()
                        this.stepTypeface(trigger, 1)
                    }
                }, true)
            })
        },
        stepTypeface(trigger, dir) {
            const sigEl = this.signatureEl
            const fonts = sigEl && sigEl.resolvedFonts
            if (!sigEl || !fonts || !fonts.length) return

            const fontNames = (this.vueformSchema.signature.fonts || [])
                .map((f) => f.split('@')[0].trim())
            const labelEl = trigger.querySelector('div')

            let base = fontNames.indexOf(sigEl.fontFamily)
            if (base === -1) {
                base = parseInt(trigger.dataset.typefaceIndex, 10)
                if (Number.isNaN(base)) base = 0
            }
            const next = Math.min(fonts.length - 1, Math.max(0, base + dir))
            trigger.dataset.typefaceIndex = String(next)

            sigEl.setFont(fonts[next])
            if (labelEl && fontNames[next]) labelEl.textContent = fontNames[next]
        },
        syncTypefaceLabel(trigger) {
            const sigEl = this.signatureEl
            if (!sigEl) return
            const fontNames = (this.vueformSchema.signature.fonts || [])
                .map((f) => f.split('@')[0].trim())
            const idx = fontNames.indexOf(sigEl.fontFamily)
            if (idx === -1) return
            const labelEl = trigger.querySelector('div')
            trigger.dataset.typefaceIndex = String(idx)
            if (labelEl && fontNames[idx]) labelEl.textContent = fontNames[idx]
        },
        setError(message) {
            this.$emit('error', message)
        },
        clearError() {
            this.$emit('error', null)
        }
    },
    template: `
        <div class="signature-field-wrapper d-ignore">
            <div v-if="consentText" class="mb-3">
                <p class="mb-0">{{ consentText }}</p>
            </div>

            <div style="width: 100%; overflow: hidden; padding: 3px;">
                <vueform
                    ref="vueform"
                    :schema="vueformSchema"
                    :display-errors="false"
                />
            </div>

            <input
                v-if="isRequired"
                type="hidden"
                :name="fieldKey + '_validation_proxy'"
                :value="signatureValue ? '1' : ''"
                required
            />

            <input
                type="hidden"
                :name="fieldKey"
                :value="signatureValue"
            />
            <input
                type="hidden"
                :name="fieldKey + '_consent'"
                :value="consentText"
            />
            <input
                type="hidden"
                :name="fieldKey + '_timestamp'"
                :value="timestamp || ''"
            />
        </div>
    `
}
